import { AllData, Filtro, Variable_indicator } from "../interfaces/Indicator";

function hexToRgb(hex: string) {
    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
        let r = parseInt(result[1], 16);
        let g = parseInt(result[2], 16);
        let b = parseInt(result[3], 16);
        return r + "," + g + "," + b;
    }
    return undefined;
}

function titleCase(string: string) {
    let stringCapitalize = string[0].toLocaleUpperCase() + string.slice(1).toLowerCase()
    return stringCapitalize
}

function getImageFormat(filename: string): string | null {
    const regex = /(?:\.([^.]+))?$/;
    const match = regex.exec(filename);

    if (match && match[1]) {
        const format = match[1].toLowerCase();
        const validFormats = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg'];

        if (validFormats.includes(format)) {
            return format;
        } else {
            return 'Invalid format';
        }
    }

    return null;
}

const getValueID = (string: string | undefined) => {
    if (string !== undefined) {
        const expresionRegular = /id(\d+)/;
        const value_ID = string.match(expresionRegular);

        if (value_ID) {
            return value_ID[1]

        } else {
            return null
        }
    } else {
        return
    }
}

type Predicate = (key: string) => boolean;

const getDomain = (data: any[], searchPredicate: Predicate): [number, number] => {
    const findKey = (item: any) => Object.keys(item).find(searchPredicate);

    const values = data
        .map(item => {
            const key = findKey(item);
            return key ? parseFloat(item[key]) : NaN;
        })
        .filter(value => !isNaN(value));

    if (values.length === 0) {
        return [0, 1];
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = (max - min) * 0.1;  // 10% padding on top and bottom
    return [min - padding, max + padding];
};

/**
 * Función de filtro de indicadores
*/

type GroupedData = { [key: string]: any };

const filterByCriteria = (
    criteria: { [key: string]: string[] },
    data: AllData[],
    filters: Filtro[],
    pivotAxisYLeft: string, // Variable pivot para agrupar los datos en el eje Y izquierdo (POR DEFECTO)
    pivotAxisYRight?: Variable_indicator | null, // Variable del eje Y derecho (opcional),
) => {
    if (!Array.isArray(data) || data.length === 0) {
        return [];
    }

    const filteredData = data.filter((item) => {
        return Object.keys(criteria).every((key) => {
            const filterValues = criteria[key]?.map((v) => v.toLowerCase()) || [];

            if (filterValues.includes('todos')) return true;

            // Extraer la parte del encabezado que corresponde al criterio
            const matchingPart = item.encabezado
                ?.toLowerCase()
                .split('-')
                .find((part) => part.split('.')[0].trim() === key.toLowerCase().trim());

            if (!matchingPart) return false;

            const extractedValue = matchingPart.split('.')[1]?.trim().toLowerCase();
            return filterValues.includes(extractedValue);
        });
    });

    const groupedData: GroupedData = {};

    filteredData.forEach(({ anio, encabezado, valor }) => {
        if (!encabezado || !valor || isNaN(parseFloat(valor))) return;

        const parts = encabezado.toLowerCase().split('-'); // Convertir encabezado a minúsculas y dividir

        const addValue = (year: string, key: string) => {
            if (!groupedData[year]) {
                groupedData[year] = { name: year };
            }
            groupedData[year][key] = (groupedData[year][key] || 0) + parseFloat(valor);
        };

        // Caso 1: Encabezado con '.'
        if (parts.length === 1 && encabezado.includes('.')) {
            const [variable, value] = encabezado.toLowerCase().split('.').map((str) => str.trim());
            if (variable === pivotAxisYLeft.toLowerCase()) {
                addValue(anio, value.charAt(0).toUpperCase() + value.slice(1));
            }

            if (pivotAxisYRight && variable === pivotAxisYRight?.titulo.toLowerCase()) {
                addValue(anio, value.charAt(0).toUpperCase() + value.slice(1));
            }
        }

        // Caso 2: Encabezados compuestos
        else if (parts.length > 1) {
            const matchingPartLeft = parts.find((part) => part.startsWith(pivotAxisYLeft.toLowerCase()));
            if (matchingPartLeft) {
                const simplifiedHeader = matchingPartLeft.includes('.')
                    ? matchingPartLeft.split('.')[1].trim().toLowerCase()
                    : matchingPartLeft.trim();

                addValue(anio, simplifiedHeader.charAt(0).toUpperCase() + simplifiedHeader.slice(1));
            }

            if (pivotAxisYRight) {
                const matchingRight = parts.find((part) => part.startsWith(pivotAxisYRight?.titulo.toLowerCase()));
                if (matchingRight) {
                    const simplified = matchingRight.includes('.') ? matchingRight.split('.')[1].trim() : matchingRight.trim();
                    addValue(anio, simplified.charAt(0).toUpperCase() + simplified.slice(1));
                }
            }
        }

        // Caso 3: Encabezados simples
        else if (parts.length === 1) {
            const key = parts[0].trim().toLowerCase();
            if (key === pivotAxisYLeft.toLowerCase()) {
                addValue(anio, key.charAt(0).toUpperCase() + key.slice(1));
            }
            if (pivotAxisYRight && key === pivotAxisYRight?.titulo.toLowerCase()) {
                addValue(anio, key.charAt(0).toUpperCase() + key.slice(1));
            }
        }
    });

    // Transformación final: Reemplazar valores NaN con null y capitalizar claves excepto "name"
    const orderMap = new Map<string, number>();

    filters.forEach((filter) => {
        filter.values.forEach((valueOption) => {
            const normalizedValue = valueOption.value.toLowerCase();
            if (valueOption.orden !== undefined) {
                orderMap.set(normalizedValue, valueOption.orden);
            }
        });
    });

    // Transformación final con ordenamiento
    return Object.values(groupedData).map((item) => {
        const capitalizedItem: { [key: string]: any } = {};

        const keys = Object.keys(item);
        const nameKey = keys.find(k => k === 'name');
        const otherKeys = keys.filter(k => k !== 'name');

        const sortedKeys = otherKeys.sort((a, b) => {
            const orderA = orderMap.get(a.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
            const orderB = orderMap.get(b.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;

            return orderA - orderB;
        });

        if (nameKey) {
            capitalizedItem[nameKey] = item[nameKey];
        }

        sortedKeys.forEach((key) => {
            const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
            const value = item[key];
            capitalizedItem[capitalizedKey] = typeof value === 'number' && isNaN(value) ? null : value;
        });

        return capitalizedItem;
    });
};

const formatObjectToString = (obj: Record<string, string>): string => {
    return Object.entries(obj)
        .map(([key, value]) => `${key}-${value}`)
        .join('_');
};

const scrollToElement = (elementId: string, offset: number = -100, delay: number = 0) => {
    setTimeout(() => {
        const element = document.getElementById(elementId);
        if (!element) {
            console.warn(`No se encontró el elemento con ID '${elementId}'`);
            return;
        }

        const elementY = element.getBoundingClientRect().top + window.scrollY + offset;

        window.scrollTo({
            top: elementY,
            behavior: 'smooth',
        });
    }, delay);
};

const normalizeString = (str: string) => {
    return str
        .toLowerCase()
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, ""); // Remueve diacríticos (acentos)
};

export { hexToRgb, titleCase, getImageFormat, getValueID, getDomain, filterByCriteria, formatObjectToString, scrollToElement, normalizeString }